// Screens 10, 11 and 18 · the shop board (Spencer's view and the tablet's), and variance.
// Cards are generated from the job model, never typed. A card is a traveler: it follows a
// cart through the steps one person runs in one go. Pulling it is the clock-in, closing it
// the clock-out. Nobody on the shop role ever sees a price here.

import { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { buildJob } from '@zerogap/db';
import { currentViews, generateCards, resolveJob, timeCategoriesFor, waitingOnCutSheet } from '@zerogap/engine';
import { useCrumb } from '../frame/Frame';
import { deleteRow, fetchRows, insertRow, insertRows, refresh, toast, updateRow, useRows, type Row } from '../lib/api';
import { longDate, qty } from '../lib/format';
import { setTick, unticked, useCardReferences } from '../lib/cardrefs';
import { cardWaiting, useInventory, type InventoryData } from '../lib/inventory';
import { jobTitle, useAllJobs } from '../lib/jobs';
import { useLibrary } from '../lib/library';
import { useSession } from '../lib/session';
import { Btn, Edit, Empty, Field, Head, Info, Ln, Loading, Modal, Panel, Seg, Tag } from '../ui/kit';
import { SopLink } from './Refs';
import { FileList, useSignedUrl, uploadFile } from './job/Files';

const hrs = (v: unknown) => `${Number(v ?? 0).toFixed(1)} h`;
const hue = (id: string) => [...id].reduce((n, c) => (n * 31 + c.charCodeAt(0)) % 360, 7);

/** Board states are names in settings; their meaning comes from their position in the list. */
function useStates() {
  const s = useSession();
  const names = s.setting<string[]>('board_states', []);
  return {
    names,
    queued: names[0] ?? 'Queued',
    active: names[1] ?? 'Active',
    done: names[names.length - 1] ?? 'Done',
    hold: names.length > 3 ? names[2]! : null,
  };
}

function useBoard() {
  const cards = useRows('work_card', { order: ['sort', 'number'] });
  const flags = useRows('card_change_flag', { eq: { acknowledged_at: null } });
  const steps = useRows('work_card_step', { order: ['sort'] });
  const centers = useRows('work_center', { order: ['kind', 'name'] });
  const users = useRows('user');
  const all = useAllJobs();
  return { cards: cards.rows, steps: steps.rows, centers: centers.rows, users: users.rows, all, flags: flags.rows, loading: cards.loading || all.loading };
}

export function Shop() {
  const s = useSession();
  const [params, setParams] = useSearchParams();
  const owner = s.can('screen.board_owner');
  const view = params.get('view') ?? (owner && s.device !== 'tablet' ? 'board' : 'tablet');
  useCrumb([{ label: 'Shop' }]);
  const tabs = [
    ...(owner ? [{ key: 'board', label: 'Board' }] : []),
    { key: 'tablet', label: owner ? 'Tablet view' : 'My work' },
    ...(s.can('screen.variance') ? [{ key: 'variance', label: 'Variance' }] : []),
  ];
  return (
    <div>
      {tabs.length > 1 && (
        <div style={{ marginBottom: 10 }}>
          <Seg items={tabs} value={view} onChange={(k) => setParams({ view: k }, { replace: true })} />
        </div>
      )}
      {view === 'board' && owner && <OwnerBoard />}
      {view === 'tablet' && <Tablet />}
      {view === 'variance' && <VarianceList />}
    </div>
  );
}

// ---------- Spencer's view ----------

/**
 * The flag on a card whose materials are not all in: it names what is missing so nobody
 * starts a card they cannot finish. It is only a flag. The card can still be pulled.
 */
/** Which cards are waiting on a cut sheet: one read of the flagged operations and of the cut sheets attached to cards. */
function useCutSheets() {
  const s = useSession();
  const kinds = s.setting<{ key: string; is_cut_sheet?: boolean }[]>('file_kinds', []).filter((k) => k.is_cut_sheet).map((k) => k.key);
  const flagged = useRows('operation', { eq: { needs_cut_sheet: true } });
  const files = useRows('file', { eq: { attached_type: 'card' }, in: { kind: kinds }, enabled: kinds.length > 0 });
  const need = useMemo(() => flagged.rows.map((o) => o.id as string), [flagged.rows]);
  return {
    kind: kinds[0] ?? null,
    waiting: (card: Row, steps: Row[]) =>
      waitingOnCutSheet(steps.map((x) => ({ operation_id: (x.operation_id as string | null) ?? null })), need, files.rows.filter((f) => f.attached_id === card.id), kinds),
  };
}

/** A change order was approved while this card was queued or in progress. It says so until the worker acknowledges it. */
function ChangedFlag({ card, flags, full }: { card: Row; flags: Row[]; full?: boolean }) {
  const s = useSession();
  const mine = flags.filter((f) => f.work_card_id === card.id && !f.acknowledged_at);
  if (mine.length === 0 && !card.non_billable) return null;
  return (
    <div>
      {card.non_billable && <Tag kind="off">no charge</Tag>}{' '}
      {mine.map((f) => (
        <div key={f.id} className={full ? 'warn' : ''}>
          <Tag kind="un">Changed in Rev {f.rev_no}</Tag>
          {full && (
            <>
              <ul style={{ margin: '4px 0 6px 18px' }}>
                {((f.changes as string[]) ?? []).map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
              <Btn small primary onClick={() => void updateRow('card_change_flag', f.id, { acknowledged_at: new Date().toISOString(), acknowledged_by: s.user?.id ?? null })}>
                I have seen this change
              </Btn>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function WaitingOnCutSheet({ waiting }: { waiting: boolean }) {
  if (!waiting) return null;
  return (
    <div>
      <Tag kind="un">waiting on cut sheet</Tag>
    </div>
  );
}

function WaitingOnParts({ inv, card }: { inv: InventoryData; card: Row }) {
  const missing = cardWaiting(inv, card);
  if (missing.length === 0) return null;
  return (
    <div>
      <Tag kind="un">waiting on parts</Tag>{' '}
      <small>
        {missing.map((m) => `${m.description} (${qty(m.qty)}${m.unit ? ` ${m.unit}` : ''})`).join(' · ')}
      </small>
    </div>
  );
}

function OwnerBoard() {
  const b = useBoard();
  const inv = useInventory();
  const cuts = useCutSheets();
  const st = useStates();
  const { lib, rows } = useLibrary();
  const s = useSession();
  const [jobFilter, setJobFilter] = useState('');
  const [stationFilter, setStationFilter] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [gen, setGen] = useState('');
  if (b.loading) return <Loading what="Loading the board" />;

  const title = (jobId: string) => b.all.jobs.find((j) => j.job.id === jobId)?.opp?.name ?? 'Job';
  const todayStr = new Date().toDateString();
  const shown = b.cards.filter((c) => (!jobFilter || c.job_id === jobFilter) && (!stationFilter || c.station_id === stationFilter));
  const inState = (name: string) => shown.filter((c) => c.state === name && (name !== st.done || (c.closed_at && new Date(c.closed_at).toDateString() === todayStr)));
  const earlier = shown.filter((c) => c.state === st.done && !(c.closed_at && new Date(c.closed_at).toDateString() === todayStr)).length;
  const occupied = b.centers.map((w) => ({ w, n: b.cards.filter((c) => c.state === st.active && c.station_id === w.id).length })).filter((x) => x.n > 0 || x.w.kind === 'station');
  const withCards = new Set(b.cards.map((c) => c.job_id));
  const candidates = b.all.jobs.filter((j) => !withCards.has(j.job.id) && j.itemCount > 0);

  const move = async (card: Row, state: string) => {
    if (card.state === state) return;
    const patch: Row = { state };
    if (state === st.hold) {
      const reason = window.prompt('Hold this card. What is it waiting on?');
      if (!reason) return;
      patch.hold_reason = reason;
    } else patch.hold_reason = null;
    if (state === st.done) {
      // a card cannot be closed from the board either while a must-tick line is unticked
      const res = b.all.jobs.find((j) => j.job.id === card.job_id)?.res;
      const missing = await unticked(card, b.steps.filter((x) => x.work_card_id === card.id), res?.items ?? []);
      if (missing.length > 0) {
        toast(`Card #${card.number} cannot close yet. Still to tick: ${missing.map((l) => l.text).join('; ')}`);
        return;
      }
      patch.closed_at = new Date().toISOString();
    }
    if (state === st.queued) Object.assign(patch, { pulled_at: null, person_id: null, station_id: null });
    await updateRow('work_card', card.id, patch);
  };

  const generate = async (jobId: string) => {
    if (!lib) return;
    const [job] = await fetchRows('job', { eq: { id: jobId } });
    if (!job) return;
    const res = resolveJob(
      lib,
      buildJob(
        {
          job,
          job_standard: await fetchRows('job_standard', { eq: { job_id: jobId } }),
          job_item: await fetchRows('job_item', { eq: { job_id: jobId } }),
          sub_line: [],
          decision_line: [],
        },
        rows.standard_def,
      ),
    );
    const made = generateCards(res, lib, { capHours: Number(s.setting('card_cap_hours', 16)), floorHours: Number(s.setting('card_floor_hours', 4)) });
    for (const [i, c] of made.entries()) {
      const card = await insertRow('work_card', { job_id: jobId, number: i + 1, name: c.name, phase_id: c.phase_id, break_label: c.break_label, state: st.queued, sort: i + 1, hours_standard: c.hours, items: c.item_ids, bom: c.bom });
      await insertRows('work_card_step', c.steps.map((x, k) => ({ work_card_id: card.id, sort: k + 1, operation_id: x.operation_id, work_center_id: x.work_center_id, name: x.operation_name, qty: x.qty, unit: x.unit, minutes_standard: x.minutes })));
      await insertRow('label', { code: `ZG-C-${String(card.id).slice(0, 8).toUpperCase()}`, kind: 'card', target_id: card.id });
    }
    setGen('');
    toast(`${made.length} cards generated.`);
  };

  return (
    <div>
      <Head title="Shop board · all jobs" sub={occupied.length ? occupied.map((x) => `${x.w.name} ${x.n}/${x.w.capacity}`).join(' · ') : 'No one has tapped in at a station.'}>
        <select className="read" value={jobFilter} onChange={(e) => setJobFilter(e.target.value)}>
          <option value="">Every job</option>
          {b.all.jobs.filter((j) => withCards.has(j.job.id)).map((j) => (
            <option key={j.job.id} value={j.job.id}>
              {jobTitle(j)}
            </option>
          ))}
        </select>
        <select className="read" value={stationFilter} onChange={(e) => setStationFilter(e.target.value)}>
          <option value="">Every station</option>
          {b.centers.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
        {candidates.length > 0 && (
          <>
            <select value={gen} onChange={(e) => setGen(e.target.value)}>
              <option value="">Generate cards for…</option>
              {candidates.map((j) => (
                <option key={j.job.id} value={j.job.id}>
                  {jobTitle(j)}
                </option>
              ))}
            </select>
            <Btn small disabled={!gen} onClick={() => void generate(gen)}>
              Generate
            </Btn>
          </>
        )}
      </Head>
      {b.cards.length === 0 && <Empty>No cards yet. Cards are generated from a job's resolved route.</Empty>}
      <div className="kan">
        {st.names.map((name) => (
          <div
            key={name}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => {
              const card = b.cards.find((c) => c.id === dragging);
              setDragging(null);
              if (card) void move(card, name);
            }}
          >
            <div className="colh">
              {name} ({inState(name).length})
            </div>
            {inState(name).map((c) => {
              const person = b.users.find((u) => u.id === c.person_id);
              const station = b.centers.find((w) => w.id === c.station_id);
              const delta = c.hours_actual !== null ? Math.round((Number(c.hours_actual) - Number(c.hours_standard)) * 60) : null;
              return (
                <div key={c.id} className={`kcard click${c.state === st.hold ? ' hold' : ''}`} style={{ borderLeftColor: `hsl(${hue(c.job_id)} 45% 45%)` }} draggable onDragStart={() => setDragging(c.id)} onDragEnd={() => setDragging(null)} onClick={() => setOpenId(c.id)}>
                  <b>
                    #{c.number} {title(c.job_id)}
                  </b>{' '}
                  · {c.name} · {hrs(c.hours_standard)}
                  {c.state === st.active && (
                    <div>
                      <small>
                        {person?.name ?? 'someone'} · at: {station?.name ?? 'station unknown'} · pulled {longDate(c.pulled_at)}
                      </small>
                    </div>
                  )}
                  {c.hold_reason && (
                    <div>
                      <small>hold: {c.hold_reason}</small>
                    </div>
                  )}
                  {c.state !== st.done && <WaitingOnParts inv={inv} card={c} />}
                  {c.state !== st.done && <WaitingOnCutSheet waiting={cuts.waiting(c, b.steps.filter((x) => x.work_card_id === c.id))} />}
                  <ChangedFlag card={c} flags={b.flags} />
                  {delta !== null && (
                    <div>
                      <small>
                        {Number(c.hours_actual).toFixed(1)} vs {Number(c.hours_standard).toFixed(1)} h {Math.abs(delta) >= 1 && <span className={delta > 0 ? 'warn' : ''}>{delta > 0 ? '+' : '−'}{Math.abs(delta)} min</span>}
                      </small>
                    </div>
                  )}
                </div>
              );
            })}
            {name === st.done && earlier > 0 && <small>{earlier} closed earlier</small>}
          </div>
        ))}
      </div>
      {openId && <CardDetail cardId={openId} board={b} onClose={() => setOpenId(null)} move={move} />}
    </div>
  );
}

function CardDetail({ cardId, board, onClose, move }: { cardId: string; board: ReturnType<typeof useBoard>; onClose: () => void; move: (card: Row, state: string) => Promise<void> }) {
  const st = useStates();
  const card = board.cards.find((c) => c.id === cardId);
  if (!card) return null;
  const steps = board.steps.filter((x) => x.work_card_id === card.id);
  const siblings = board.cards.filter((c) => c.job_id === card.job_id);
  const next = siblings.filter((c) => c.sort > card.sort && c.state === card.state).sort((a, b) => a.sort - b.sort)[0];
  const j = board.all.jobs.find((x) => x.job.id === card.job_id);

  const reorder = async (dir: -1 | 1) => {
    const same = board.cards.filter((c) => c.state === card.state).sort((a, b) => a.sort - b.sort || a.number - b.number);
    const i = same.findIndex((c) => c.id === card.id);
    const other = same[i + dir];
    if (!other) return;
    await updateRow('work_card', card.id, { sort: other.sort });
    await updateRow('work_card', other.id, { sort: card.sort === other.sort ? card.sort + dir * -1 : card.sort });
  };
  const split = async (after: number) => {
    const move2 = steps.slice(after + 1);
    if (move2.length === 0) return;
    const number = Math.max(...siblings.map((c) => c.number)) + 1;
    const mins = move2.reduce((n, x) => n + Number(x.minutes_standard), 0);
    const made = await insertRow('work_card', { job_id: card.job_id, number, name: `${card.name} (part 2)`, phase_id: card.phase_id, break_label: card.break_label, state: card.state, sort: card.sort + 1, hours_standard: (mins / 60).toFixed(2), items: card.items, bom: [] });
    for (const x of move2) await updateRow('work_card_step', x.id, { work_card_id: made.id });
    await updateRow('work_card', card.id, { hours_standard: (Number(card.hours_standard) - mins / 60).toFixed(2) });
    await insertRow('label', { code: `ZG-C-${String(made.id).slice(0, 8).toUpperCase()}`, kind: 'card', target_id: made.id });
  };
  const merge = async () => {
    if (!next) return;
    const theirs = board.steps.filter((x) => x.work_card_id === next.id);
    for (const [k, x] of theirs.entries()) await updateRow('work_card_step', x.id, { work_card_id: card.id, sort: steps.length + k + 1 });
    await updateRow('work_card', card.id, { hours_standard: (Number(card.hours_standard) + Number(next.hours_standard)).toFixed(2), bom: [...(card.bom ?? []), ...(next.bom ?? [])], items: [...new Set([...(card.items ?? []), ...(next.items ?? [])])] });
    await deleteRow('work_card', next.id);
  };

  return (
    <Modal title={`#${card.number} ${j?.opp?.name ?? ''} · ${card.name}`} onClose={onClose}>
      <div className="row mid" style={{ marginBottom: 10, gap: 6 }}>
        <select value={card.state} onChange={(e) => void move(card, e.target.value)}>
          {st.names.map((n) => (
            <option key={n}>{n}</option>
          ))}
        </select>
        <Btn small onClick={() => void reorder(-1)}>
          Earlier
        </Btn>
        <Btn small onClick={() => void reorder(1)}>
          Later
        </Btn>
        {next && (
          <Btn small onClick={() => window.confirm(`Merge card #${next.number} into this one?`) && void merge()}>
            Merge with #{next.number}
          </Btn>
        )}
        <Link className="btn sm" to={`/jobs/${card.job_id}`}>
          Open job
        </Link>
      </div>
      <CardBody card={card} steps={steps} board={board} onSplit={card.state === st.queued || card.state === st.hold ? split : undefined} />
    </Modal>
  );
}

/** What a card carries: its steps as reference lines, its items on the marked view, its materials and its files. */
function CardBody({ card, steps, board, onSplit }: { card: Row; steps: Row[]; board: ReturnType<typeof useBoard>; onSplit?: (afterIndex: number) => Promise<void> }) {
  const items = useRows('job_item', { in: { id: (card.items as string[]) ?? [] } });
  const views = useRows('view', { eq: { job_id: card.job_id } });
  // only the sheets in force: one a later revision replaced never shows on a card
  const inForce = currentViews(views.rows as any[]);
  const view = inForce.find((v) => v.is_primary) ?? inForce[0];
  const marks = useRows('mark', { eq: { view_id: view?.id }, enabled: !!view });
  const file = useRows('file', { eq: { id: view?.file_id }, enabled: !!view?.file_id }).rows[0];
  const url = useSignedUrl(file?.storage_path);
  const mine = marks.rows.filter((m) => ((card.items as string[]) ?? []).includes(m.job_item_id));
  const bom = ((card.bom as Row[]) ?? []).filter((x) => Number(x.qty) > 0);
  const s = useSession();
  const refs = useCardReferences(card, steps);
  const cuts = useCutSheets();
  const cutInput = useRef<HTMLInputElement>(null);
  const needsCut = cuts.waiting(card, steps);
  const attachCut = async (list: FileList | null) => {
    const picked = list?.[0];
    if (!picked) return;
    await uploadFile(picked, 'card', card.id, s.user?.id ?? null, cuts.kind);
    if (cutInput.current) cutInput.current.value = '';
    toast('Cut sheet attached to this card.');
  };
  const canTick = useSession().canWrite && card.state !== useStates().done;
  return (
    <div className="row">
      <div className="col">
        <ChangedFlag card={card} flags={board.flags} full />
        {card.reason && (
          <p style={{ marginBottom: 8 }}>
            <b>{card.origin === 'warranty' ? 'Warranty fix' : 'Rework'}:</b> {card.reason}
          </p>
        )}
        <div className="lb">
          <span>Steps · for reference, nothing to tick</span>
          <Link className="tag off" to={`/shop/cards/${card.id}/traveler?print=1`}>
            Print traveler
          </Link>
        </div>
        {steps.map((x, i) => (
          <Ln
            key={x.id}
            left={
              <>
                {x.name} <small>· {qty(x.qty)} {x.unit ?? ''}</small>
                {refs.sopsFor(x.operation_id).map((sop) => (
                  <span key={sop.id}>
                    {' '}
                    <SopLink sop={sop}>
                      <span className="tag on">SOP · {sop.title}</span>
                    </SopLink>
                  </span>
                ))}
              </>
            }
            right={
              <span className="nowrap">
                {board.centers.find((w) => w.id === x.work_center_id)?.name ?? '—'} <small>· {Math.round(Number(x.minutes_standard))} min</small>{' '}
                {onSplit && i < steps.length - 1 && (
                  <button type="button" className="tag off" title="End this card after this step" onClick={() => window.confirm('Split the card after this step?') && void onSplit(i)}>
                    split
                  </button>
                )}
              </span>
            }
          />
        ))}
        {refs.assembled.checklist.length > 0 && (
          <>
            <div className="lb" style={{ marginTop: 12 }}>
              <span>Checklist{refs.canClose.ok ? '' : ` · ${refs.canClose.missing.length} to tick before this card can close`}</span>
            </div>
            {refs.assembled.checklist.map((c) => (
              <label key={c.line.id} className="check" style={{ display: 'flex', padding: '4px 0' }}>
                <input type="checkbox" disabled={!canTick} checked={refs.ticked.has(c.line.id)} onChange={(e) => void setTick(card.id, c.line.id, e.target.checked, s.user?.id ?? null, refs.ticks)} />
                <span>
                  {c.line.text}
                  {c.item_numbers.length > 0 && <small> · {c.item_numbers.join(', ')}</small>} {c.line.required && <Tag kind={refs.ticked.has(c.line.id) ? 'on' : 'un'}>must tick</Tag>}
                </span>
              </label>
            ))}
            {refs.assembled.problems.map((p) => (
              <div key={p} className="warn">
                {p}
              </div>
            ))}
          </>
        )}
        {bom.length > 0 && (
          <>
            <div className="lb" style={{ marginTop: 12 }}>
              <span>Materials this card takes from stock</span>
            </div>
            {bom.map((x, i) => (
              <Ln key={i} left={x.description} right={`${qty(x.qty)} ${x.unit ?? ''}`} />
            ))}
          </>
        )}
      </div>
      <div className="col">
        <div className="lb">
          <span>Items · {items.rows.map((i) => i.number).join(', ') || '—'}</span>
        </div>
        {view && url ? (
          <div className="pic">
            <img src={url} alt="" />
            {mine.map((m) => (
              <span key={m.id} className="mk hl" style={{ left: `${m.x_pct}%`, top: `${m.y_pct}%` }}>
                {items.rows.find((i) => i.id === m.job_item_id)?.number}
              </span>
            ))}
          </div>
        ) : (
          <Empty>No picture for this job yet.</Empty>
        )}
        {items.rows.filter((i) => i.note).map((i) => (
          <div key={i.id}>
            <small>
              <b>{i.number}</b> · {i.note}
            </small>
          </div>
        ))}
        <div className="lb" style={{ marginTop: 12 }}>
          <span>Files on this card</span>
        </div>
        {needsCut && (
          <div className="row mid" style={{ gap: 8, marginBottom: 8 }}>
            <Tag kind="un">waiting on cut sheet</Tag>
            <input ref={cutInput} type="file" accept="application/pdf,image/*" className="hide" onChange={(e) => void attachCut(e.target.files)} />
            <Btn small onClick={() => cutInput.current?.click()}>
              Attach the cut sheet
            </Btn>
            <small>This card has a step that needs one. It can still be pulled.</small>
          </div>
        )}
        <FileList attachedType="card" attachedId={card.id} />
      </div>
    </div>
  );
}

// ---------- the tablet: pull forward ----------

function Tablet() {
  const b = useBoard();
  const inv = useInventory();
  const cuts = useCutSheets();
  const st = useStates();
  const s = useSession();
  const entries = useRows('time_entry', { eq: { person_id: s.user?.id }, enabled: !!s.user });
  const [closing, setClosing] = useState<Row | null>(null);
  const [logging, setLogging] = useState(false);
  const me = s.user;
  const stations = useMemo(() => b.centers.filter((w) => w.kind === 'station'), [b.centers]);
  if (b.loading) return <Loading what="Loading the board" />;

  const title = (jobId: string) => b.all.jobs.find((j) => j.job.id === jobId)?.opp?.name ?? 'Job';
  const mine = b.cards.filter((c) => c.state === st.active && c.person_id === me?.id);
  const queue = b.cards.filter((c) => c.state === st.queued).sort((a, c) => a.sort - c.sort || a.number - c.number);

  const pull = async (card: Row) => {
    const now = new Date().toISOString();
    await updateRow('work_card', card.id, { state: st.active, pulled_at: now, person_id: me?.id ?? null });
    await insertRow('time_entry', { kind: 'card', work_card_id: card.id, job_id: card.job_id, person_id: me?.id ?? null, started_at: now });
  };
  const at = async (card: Row, stationId: string) => {
    await updateRow('work_card', card.id, { station_id: stationId });
  };

  return (
    <div className="tablet stack">
      <Head title={`${me?.name ?? 'Shop'} · ${new Date().toLocaleDateString('en-US', { weekday: 'short' })}`}>
        {s.can('log_time') && <Btn onClick={() => setLogging(true)}>Log time</Btn>}
      </Head>
      {mine.length === 0 && (
        <Panel>
          <Empty>No active card. Pull the next one below: the pull is the clock-in.</Empty>
        </Panel>
      )}
      {mine.map((card) => {
        const steps = b.steps.filter((x) => x.work_card_id === card.id);
        const started = entries.rows.find((e) => e.work_card_id === card.id && !e.ended_at)?.started_at ?? card.pulled_at;
        const elapsed = started ? (Date.now() - new Date(started).getTime()) / 3600000 : 0;
        const places = [...new Map([...steps.map((x) => b.centers.find((w) => w.id === x.work_center_id)), ...stations].filter((w): w is Row => !!w).map((w) => [w.id, w])).values()];
        return (
          <Panel key={card.id} soft label={`Active · #${card.number} ${title(card.job_id)} · ${card.name} · ${elapsed.toFixed(1)} of ${hrs(card.hours_standard)}`}>
            <WaitingOnParts inv={inv} card={card} />
            <WaitingOnCutSheet waiting={cuts.waiting(card, steps)} />
            <CardBody card={card} steps={steps} board={b} />
            <div className="row mid" style={{ marginTop: 12, gap: 8 }}>
              {places.map((w) => (
                <Btn key={w.id} primary={card.station_id === w.id} onClick={() => void at(card, w.id)}>
                  At: {w.name}
                </Btn>
              ))}
              <span className="grow" />
              <Btn primary big onClick={() => setClosing(card)}>
                Close card
              </Btn>
            </div>
          </Panel>
        );
      })}
      <Panel label={`Next up (${queue.length})`}>
        {queue.length === 0 && <Empty>Nothing queued.</Empty>}
        {queue.map((c, i) => (
          <Ln
            key={c.id}
            left={
              <>
                <b>
                  #{c.number} {title(c.job_id)}
                </b>{' '}
                · {c.name} · {hrs(c.hours_standard)}
                <WaitingOnParts inv={inv} card={c} />
                <WaitingOnCutSheet waiting={cuts.waiting(c, b.steps.filter((x) => x.work_card_id === c.id))} />
                <ChangedFlag card={c} flags={b.flags} />
              </>
            }
            right={
              <Btn primary={i === 0} onClick={() => void pull(c)}>
                Pull
              </Btn>
            }
          />
        ))}
      </Panel>
      {closing && <CloseCard card={closing} onClose={() => setClosing(null)} />}
      {logging && <LogTime onClose={() => setLogging(false)} />}
    </div>
  );
}

/** Close → "used anything extra? scan or skip" → done. Closing consumes the card's BOM from stock. */
function CloseCard({ card, onClose }: { card: Row; onClose: () => void }) {
  const st = useStates();
  const s = useSession();
  const planned =((card.bom as Row[]) ?? []).filter((x) => x.material_id && Number(x.qty) > 0);
  const [used, setUsed] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const cardSteps = useRows('work_card_step', { eq: { work_card_id: card.id }, order: ['sort'] });
  const refs = useCardReferences(card, cardSteps.rows);

  const finish = async () => {
    setBusy(true);
    try {
      const now = new Date();
      const entry = (await fetchRows('time_entry', { eq: { work_card_id: card.id }, order: ['-started_at'] })).find((e) => !e.ended_at);
      const mins = entry?.started_at ? Math.max(0, (now.getTime() - new Date(entry.started_at).getTime()) / 60000) : 0;
      if (entry) await updateRow('time_entry', entry.id, { ended_at: now.toISOString(), minutes: mins.toFixed(1), station_id: card.station_id ?? null });
      const consumed: Row[] = [];
      for (const line of planned) {
        const amount = Number(used[line.material_id] ?? line.qty);
        if (!(amount > 0)) continue;
        consumed.push({ material_id: line.material_id, description: line.description, qty: amount });
        // the movement is the record; the database keeps stock on hand in step with it
        await insertRow('stock_movement', { material_id: line.material_id, kind: 'consume', qty: -amount, reason: 'card closed', work_card_id: card.id, job_id: card.job_id, by_user_id: s.user?.id ?? null });
      }
      if (consumed.length > 0) refresh('material');
      const actual = mins / 60;
      await updateRow('work_card', card.id, { state: st.done, closed_at: now.toISOString(), hours_actual: actual.toFixed(2), consumed_bom: consumed });

      // the why-question: over by both the percent and the minutes in settings; a big under-run is a standards-review candidate
      const std = Number(card.hours_standard) * 60;
      const over = mins - std;
      const pct = std > 0 ? (over / std) * 100 : 0;
      const limit = s.setting<{ pct: number; minutes: number }>('variance_text_threshold', { pct: 0, minutes: 0 });
      const under = Number(s.setting('underrun_flag_pct', 0));
      if (limit.pct > 0 && pct >= limit.pct && over >= limit.minutes) {
        await insertRow('variance_flag', { work_card_id: card.id, job_id: card.job_id, minutes_over: over.toFixed(1), pct_over: pct.toFixed(1) });
      } else if (under > 0 && std > 0 && pct <= -under) {
        await insertRow('variance_flag', { work_card_id: card.id, job_id: card.job_id, minutes_over: over.toFixed(1), pct_over: pct.toFixed(1), recommend_review: true });
      }
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Close card #${card.number}`} onClose={onClose}>
      {!refs.canClose.ok && (
        <div className="warn" style={{ marginBottom: 10 }}>
          This card cannot close yet. Still to tick on its checklist: {refs.canClose.missing.map((l) => l.text).join('; ')}
        </div>
      )}
      <p style={{ marginBottom: 10 }}>Used anything extra, or less? Change the amount, or skip.</p>
      {planned.length === 0 && <Empty>This card takes nothing from stock.</Empty>}
      {planned.map((line) => (
        <Ln
          key={line.material_id}
          left={line.description}
          right={
            <span className="nowrap">
              <input type="number" className="short" value={used[line.material_id] ?? String(line.qty)} onChange={(e) => setUsed({ ...used, [line.material_id]: e.target.value })} /> {line.unit ?? ''}
            </span>
          }
        />
      ))}
      <div className="row mid" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
        <Btn primary big disabled={busy || refs.loading || !refs.canClose.ok} onClick={() => void finish()}>
          Done
        </Btn>
      </div>
    </Modal>
  );
}

/** Time that is not a card: cleaning, a blade change, a site errand. */
function LogTime({ onClose }: { onClose: () => void }) {
  const s = useSession();
  const every = useRows('shop_time_category', { order: ['sort'] });
  const assignments = useRows('time_category_assignment');
  // the list this person was given, or their role's; with none assigned, every category
  const cats = { rows: timeCategoriesFor({ id: s.user?.id ?? '', role: s.role ?? null }, assignments.rows as any[], every.rows as any[]) as Row[] };
  const all = useAllJobs();
  const [mins, setMins] = useState('');
  const [cat, setCat] = useState('');
  const [job, setJob] = useState('');
  const [op, setOp] = useState('');
  // the operations of the chosen job, so the time can be compared with what the job allows for them
  const jobOps = useMemo(() => {
    const res = all.jobs.find((j) => j.job.id === job)?.res;
    const seen = new Map<string, string>();
    for (const g of res?.groups ?? []) for (const st of g.steps) if (Number(st.minutes) > 0) seen.set(st.operation_id, st.operation_name);
    return [...seen].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [all.jobs, job]);
  const [note, setNote] = useState('');
  return (
    <Modal title="Log time" onClose={onClose}>
      <div className="form">
        <Field label="Minutes">
          <input type="number" autoFocus value={mins} onChange={(e) => setMins(e.target.value)} />
        </Field>
        <Field label="Category">
          <select value={cat} onChange={(e) => setCat(e.target.value)}>
            <option value="">{cats.rows.length ? 'Pick one' : 'None named yet (Library → Time categories)'}</option>
            {cats.rows.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Job">
          <select value={job} onChange={(e) => { setJob(e.target.value); setOp(''); }}>
            <option value="">Non-project</option>
            {all.jobs.map((j) => (
              <option key={j.job.id} value={j.job.id}>
                {jobTitle(j)}
              </option>
            ))}
          </select>
        </Field>
        {job && jobOps.length > 0 && (
          <Field label="Operation (optional)">
            <select value={op} onChange={(e) => setOp(e.target.value)}>
              <option value="">Not tied to one operation</option>
              {jobOps.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Note" wide>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
      <div style={{ marginTop: 12 }}>
        <Btn
          primary
          big
          disabled={!(Number(mins) > 0)}
          onClick={async () => {
            await insertRow('time_entry', { kind: 'manual', person_id: s.user?.id ?? null, job_id: job || null, shop_time_category_id: cat || null, operation_id: op || null, minutes: Number(mins), note: note.trim() || null, ended_at: new Date().toISOString() });
            toast('Time logged.');
            onClose();
          }}
        >
          Log it
        </Btn>
      </div>
    </Modal>
  );
}

// ---------- variance ----------

export function VarianceList({ jobId }: { jobId?: string }) {
  const flags = useRows('variance_flag', { ...(jobId ? { eq: { job_id: jobId } } : {}), order: ['-created_at'] });
  const cards = useRows('work_card');
  const all = useAllJobs();
  const s = useSession();
  const limit = s.setting<{ pct: number; minutes: number }>('variance_text_threshold', { pct: 0, minutes: 0 });
  const open = flags.rows.filter((f) => f.status === 'open');
  return (
    <Panel
      label={`Variance · open flags (${open.length})`}
      right={
        <Info left>
          A closed card over its standard by both {limit.pct}% and {limit.minutes} minutes raises a flag and asks why. A card far under its standard lands here as a standards-review candidate. Standards never change on their own: a person approves the change in the
          Library. The text message to Spencer is not connected yet; this panel is the backstop.
        </Info>
      }
    >
      {flags.rows.length === 0 && <Empty>No flags. A flag appears when a card closes well over or well under its standard time.</Empty>}
      {flags.rows.map((f) => {
        const card = cards.rows.find((c) => c.id === f.work_card_id);
        const j = all.jobs.find((x) => x.job.id === f.job_id);
        const over = Number(f.minutes_over);
        return (
          <Ln
            key={f.id}
            className={f.status === 'open' ? 'ov' : ''}
            left={
              <>
                <b>
                  {j?.opp?.name ?? 'Job'} #{card?.number ?? '?'}
                </b>{' '}
                · {card?.name ?? ''} · {over > 0 ? '+' : '−'}
                {Math.abs(Math.round(over))} min ({Number(f.pct_over).toFixed(0)}%)
                {f.status !== 'open' && f.answer && (
                  <div>
                    <small>Answered: {f.answer}</small>
                  </div>
                )}
              </>
            }
            right={
              f.status === 'open' ? (
                <span className="nowrap">
                  {f.recommend_review && <Tag kind="un">under-run · review the standard</Tag>}{' '}
                  <Edit className="short" value="" placeholder="what happened?" onSave={(v) => (v ? updateRow('variance_flag', f.id, { answer: v, status: 'answered', answered_at: new Date().toISOString() }) : undefined)} />
                </span>
              ) : (
                <small>{f.status}</small>
              )
            }
          />
        );
      })}
    </Panel>
  );
}
